import React, { useCallback, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from 'antd'
import PageElementsProvider from '../../../context/pageElementsContext';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS, TEMPLATE_TYPE } from '../../../context/complexArtboardContext';
import ComplexEditingProvider from '../../../context/complexEditingContext';
import { groupRows, getRangeOfStaticTable } from '../complexDocumentHelper';
import { CreativeBlockContainer, StaticTable } from '../styles';
import BlockHighlight from '../BlockHighlight';
import GridContextMenu from './GridContextMenu';
import GridFormatting from './GridFormatting';
import GridRowOptions from './GridRowOptions';
import CellContainer from './CellContainer';
import ContextMenu from '../../../artboard/ContextMenu';
import { dummyCallback, COMPLEX_DOCUMENT_PAGE_WRAPPER, STATIC_TABLE_BLOCK, STATIC_TABLE_ELEMENT,FILLABLE_ELEMENT, ELEMENT_TYPES } from '../../../utils/complexDocumentConstants';
import { AccessibilitySeparatorTag } from '../ComplexBlockRenderer';

const NAMESPACES = ['DYNAMIC_TABLE_SETTINGS_SIDEBAR', 'DESIGN_ELEMENT_LAYER_DROPDOWN', 'DYNAMIC_TABLE_OPERATIONS'];

const GridDesign = (props) => {
  const { editable, itemUUID, pageId, parentUUID, getElementStyles, onClickBlockSelection, type, scaling, adjustScroll, blockIsEditable = true, isInteractiveDesign = false, showVersionSettings } = props;
  const [ selectedCell, setSelectedCell ] = useState(undefined);
  const [ selectedRange, setSelectedRange ] = useState(undefined);
  const [ showContextMenu, setContextMenu ] = useState(false);
  const [selectedEditor, setSelectedEditor] = useState(null);
  const [isOverCellUUID, setIsOverCellUUID] = useState('');
  const { t } = useTranslation(NAMESPACES);

  const tableRef = useRef(null);
  const copiedCellsUUID = useRef(null);
  const moveableRef = useRef(null);

  const customSelector = useCallback(store => store[itemUUID], []);
  const [gridBlock, methods] = useDocumentContext(customSelector, `GridDesign-${itemUUID}`);

  const artboardContextSelector = useCallback(store => store[CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU] && store[CONTEXT_KEYS.CURRENT_BLOCK] === itemUUID, []);
  const [currentBlock, updateArtboard] = useArtboardContext(artboardContextSelector, `GridDesign-${itemUUID}`);

  const currentDesignSelector = useCallback((store) => [TEMPLATE_TYPE.DOCUMENT, TEMPLATE_TYPE.FLOW].includes(store[CONTEXT_KEYS.CURRENT_DESIGN]), []);
  const [isDocumentOrFlowDesign] = useArtboardContext(currentDesignSelector, `GridDesign-${itemUUID}`);

  const selectedBlockSelector = useCallback(store => store[CONTEXT_KEYS.SELECTED_BLOCKS], []);
  const [selectedBlocks] = useArtboardContext(selectedBlockSelector, `TextBlock-${itemUUID}`);

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(customVarSelector);

  const selectedElementSelector = useCallback(store => store[selectedEditor], [selectedEditor]);
  const [selectedElement] = useDocumentContext(selectedElementSelector, `GridDesign-${selectedEditor}`);

  const checkAccessibilitySelector = useCallback(store => store[CONTEXT_KEYS.CHECK_ACCESSIBILITY] , []);
  const [isAccessibility] = useArtboardContext(checkAccessibilitySelector, `BlockRenderer-${props.itemUUID}`);

  useEffect(() => {
    if (!currentBlock) {
      setSelectedEditor(null);
      setContextMenu(false);
      setSelectedCell(undefined);
      setSelectedRange(undefined);
    }
  }, [currentBlock]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
        event.preventDefault();
        let maxRow = 0;
        let maxColumn = 0;
        for (let cell of gridBlock.contents) {
          maxRow = Math.max(maxRow, cell.row + cell.rowSpan - 1);
          maxColumn = Math.max(maxColumn, cell.column + cell.columnSpan - 1);
        }
        const firstCell = gridBlock.contents.find(cell => cell.row === 1 && cell.column === 1);
        let lastCell;
        for (let i = gridBlock.contents.length - 1; i >= 0; i--) {
          const cell = gridBlock.contents[i];
          if (cell.row + cell.rowSpan - 1 === maxRow && cell.column + cell.columnSpan - 1 === maxColumn) {
            lastCell = cell;
            break;
          }
        }
        setSelectedRange({
          start: firstCell.uuid,
          end: lastCell.uuid
        });
      }
    };
    if(currentBlock && selectedEditor === null) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentBlock, selectedEditor]);

  const updateGridBlockContents = useCallback((contents) => {
    methods.updateBlock('contents', itemUUID, { contents, flowUUID: parentUUID, type }, true, globalVariables);
  }, [parentUUID, globalVariables]);

  const updateSidebar = useCallback(() => {
    updateArtboard({
      [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: true,
      [CONTEXT_KEYS.CURRENT_BLOCK]: itemUUID,
      [CONTEXT_KEYS.CURRENT_BLOCK_TYPE]: STATIC_TABLE_BLOCK,
      [CONTEXT_KEYS.CURRENT_PAGE]: parentUUID,
      [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
      [CONTEXT_KEYS.BLOCK_SETTINGS_VISIBLE]: false
    });
  }, [parentUUID]);

  const onRightClick = useCallback((e) => {
    setContextMenu(true);
  }, []);

  const onCellDoubleClick = useCallback((e) => {
    if (!tableRef.current?.contains(e.target)) return;
    e.persist();
    let editorId;
    let targetEl = e.target;
    while (targetEl?.tagName !== 'TD') {
      targetEl = targetEl.parentNode;
    }
    const cellUUID = targetEl.id;
    setSelectedEditor(cellUUID);
    setSelectedRange(undefined);
    setSelectedCell(undefined);
    setContextMenu(false);
    const selectedEl = methods.get()[cellUUID];
    if (
      selectedEl?.contentType === FILLABLE_ELEMENT ||
      selectedEl?.contentType === ELEMENT_TYPES.CHART
    ) {
      updateArtboard({
        [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: true,
      });
    }
  }, [currentBlock]);

  const onCellSingleClick = useCallback((e) => {
    if (!tableRef.current?.contains(e.target)) return;
    e.persist();
    let editorId;
    let targetEl = e.target;
    while (targetEl?.tagName !== 'TD') {
      targetEl = targetEl.parentNode;
    }
    setContextMenu(false);
    const cellUUID = targetEl.id;
    if (e.nativeEvent.shiftKey) {
      setSelectedRange(currentRange => {
        if (currentRange?.start) {
          return Object.assign({}, currentRange, {
            end: cellUUID
          })
        }
        return currentRange;
      });
    } else {
      setSelectedEditor(currentEditor => {
        if (cellUUID === currentEditor) return currentEditor;
        updateArtboard({
          [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false
        });
        setSelectedCell(cellUUID);
        setSelectedRange({
          start: cellUUID
        });
        return null;
      });
    }
  }, []);

  let rangeCellsDimensions = undefined;
  const blockSelected = selectedBlocks.includes(itemUUID);
  const rowContents = groupRows(gridBlock.contents);

  const startCell = selectedRange?.start ? gridBlock.contents.find(cell => cell.uuid === selectedRange.start) : undefined;
  let rowStartIndex = startCell?.row - 1, colStartIndex = startCell?.column - 1;
  if (currentBlock && selectedRange?.end) {
    const endCell = gridBlock.contents.find(cell => cell.uuid === selectedRange.end);
    const {
      rowStartIndex: mergeRowStart,
      rowEndIndex,
      colStartIndex: mergeColStart,
      colEndIndex,
    } = getRangeOfStaticTable(gridBlock, selectedRange, true);
    rowStartIndex = mergeRowStart;
    colStartIndex = mergeColStart;

    rangeCellsDimensions = {
      width: Array.from(tableRef.current.querySelectorAll('col')).reduce((acc, w, wIndex) => {
        if (wIndex >= colStartIndex && wIndex <= colEndIndex) {
          return acc + w.clientWidth;
        }
        return acc;
      }, 0),
      height: Array.from(tableRef.current.querySelectorAll('tr')).reduce((acc, h, hIndex) => {
        if (hIndex >= rowStartIndex && hIndex <= rowEndIndex) {
          return acc + h.clientHeight;
        }
        return acc;
      }, 0)
    }
  }

  const isElementPresent = (cellContentUUID) => {
    const element = methods.get()[cellContentUUID]
    return element;
  }

  return (
    <CreativeBlockContainer
      id={itemUUID}
      className={'selectable'}
      data-element-type={STATIC_TABLE_ELEMENT}
      currentBlock={currentBlock}
      padding={gridBlock.style.padding}
      style={{
        background: blockSelected ? '#ACCEF7' : gridBlock.style.backgroundColor
      }}
      staticTableBlock={true}
      onClick={editable && isDocumentOrFlowDesign && !currentBlock ? (e) => {
        if (e.ctrlKey || e.metaKey) {
          onClickBlockSelection({itemUUID, parentUUID})
        }
      } : undefined}
      onDoubleClick={editable && blockIsEditable ? updateSidebar : dummyCallback}
      onMouseMove={(e) => (editable || isInteractiveDesign || showVersionSettings) && !currentBlock && getElementStyles && getElementStyles(e, itemUUID)}
      onMouseEnter={() => {
        const el = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER);
        if (el && document.activeElement !== el && !el.contains(document.activeElement)) {
          el.focus();
        }
      }}
    >
      <ComplexEditingProvider>
      {currentBlock && gridBlock.style.backgroundColor === undefined && (
         <BlockHighlight pageUUID={parentUUID} />
      )}
    <PageElementsProvider isEmail={true} isComplexDesign={true} parentElements={[gridBlock]} setParentElements={updateGridBlockContents}>
      <StaticTable id={`table-${itemUUID}`} ref={tableRef} border={gridBlock.style.border} onContextMenu={onRightClick} className={'block-table'}>
        <colgroup>
          {gridBlock.style.tableProperties.columnWidths.map((colWidth, headerIndex) => {
            return (
              <col style={{ width: `${colWidth}%`}} />
            )
          })}
        </colgroup>
        {Object.values(rowContents).map((cells, rowIndex) => {
          if (Object.keys(cells).length === 0) return (<tr></tr>);
          let cellRight = 0;
          const orderedCells = cells.sort((a, b) => {
            if (a.column > b.column) {
              return 1;
            } else if (a.column < b.column) {
              return -1;
            }
            return 0;
          });
          return (
            <tr>
              {orderedCells.map((cellContent, cellIndex) => {
                const rowHeight = cellContent.rowSpan > 1 ? gridBlock.style.tableProperties.rowHeights.reduce((acc, rowHeight, rowHeightIndex) => {
                  if (rowHeightIndex >= cellContent.row - 1 && rowHeightIndex < cellContent.row + cellContent.rowSpan - 1) {
                    return acc + rowHeight;
                  }
                  return acc;
                }, 0) : gridBlock.style.tableProperties.rowHeights[cellContent.row - 1];
                return (
                  <>
                  <td
                    id={cellContent.uuid}
                    rowspan={cellContent.rowSpan}
                    colspan={cellContent.columnSpan}
                    key={cellContent.uuid}
                    style={{
                      width: `${gridBlock.style.tableProperties.columnWidths[cellContent.column - 1]}%`,
                      height: rowHeight,
                      borderColor: cellContent.style.border?.borderColor || 'black',
                      borderStyle: 'solid',
                      borderTopWidth: cellContent.style.border?.top ? '1px' : 0,
                      borderRightWidth: cellContent.style.border?.right ? '1px' : 0,
                      borderBottomWidth: cellContent.style.border?.bottom ? '1px' : 0,
                      borderLeftWidth: cellContent.style.border?.left ? '1px' : 0,
                      backgroundColor: cellContent.style.backgroundColor || 'transparent',
                      padding: (isOverCellUUID === cellContent.uuid) ? '0px' : `${cellContent.style.padding.top}px ${cellContent.style.padding.right}px ${cellContent.style.padding.bottom}px ${cellContent.style.padding.left}px`,
                      position: 'relative',
                      // verticalAlign: `${cellContent.style.verticalAlignment || 'middle'}`
                  }}
                    onClick={currentBlock ? onCellSingleClick : dummyCallback}
                    onDoubleClick={currentBlock ? onCellDoubleClick : dummyCallback}
                  >
                    {/*(cellContent.contents[0]?.content !== '' || (selected && selectedEditor === cellContent.uuid)) && <TextElement // Reusing existing component since additional styling values of x, y will not change with text
                      element={cellContent}
                      editable={selected && editable}
                      scaling={scaling}
                      selected={selected && selectedEditor === cellContent.uuid}
                      pageId={pageId}
                      adjustScroll={adjustScroll}
                      isFromCreativeBlock={false}
                      key={cellContent.uuid}
                      isStandalone= {creativeBlock?.isStandalone}
                      isHeaderEelement = {creativeBlock?.type === HEADER || creativeBlock?.type === FOOTER}
                    />*/}
                    <CellContainer
                      key={cellContent.uuid}
                      cellPadding={`${cellContent.style.padding.top}px ${cellContent.style.padding.right}px ${cellContent.style.padding.bottom}px ${cellContent.style.padding.left}px`}
                      elementUUID={cellContent.uuid}
                      parentBlockUUID={itemUUID}
                      parentUUID={parentUUID}
                      selected={selectedEditor === cellContent.uuid}
                      scaling={scaling}
                      pageId={pageId}
                      adjustScroll={adjustScroll}
                      editable={editable && currentBlock}
                      verticalAlignment={cellContent.style.verticalAlignment}
                      horizontalAlignment={cellContent.style?.horizontalAlignment}
                      onCellDoubleClick={onCellDoubleClick}
                      setIsOverCellUUID={setIsOverCellUUID}
                      ref={moveableRef}
                      t={t}
                    />
                    {isAccessibility && isElementPresent(cellContent.uuid) && (
                      <AccessibilitySeparatorTag accessibleInfo={{ accessibleType: 'staticTable', level: null, row: cellContent.row, col: cellContent.column }} />
                    )}
                    {currentBlock && (selectedCell === cellContent.uuid ||selectedEditor === cellContent.uuid) && (
                      <div
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          outline: selectedEditor === cellContent.uuid ? '1px solid #00c4cc' : '1px dashed #0de0e9e6',
                          pointerEvents: 'none',
                          top: 0,
                          left: 0
                        }}
                      />
                    )}
                    {currentBlock && rowStartIndex + 1 === cellContent.row && colStartIndex + 1 === cellContent.column
                      && selectedRange?.end && (
                      <div
                        style={{
                          position: 'absolute',
                          width: `${rangeCellsDimensions?.width}px`,
                          height: `${rangeCellsDimensions?.height}px`,
                          backgroundColor: '#0de0e926',
                          pointerEvents: 'none',
                          zIndex: 1,
                          left: 0,
                          top: 0
                        }}
                      />
                    )}
                  </td>
                  </>
                )
              })}
              {currentBlock && rowIndex === 0 && (
                <>
                  <GridRowOptions
                    t={t}
                    itemUUID={itemUUID}
                    setSelectedRange={setSelectedRange}
                    setContextMenu={setContextMenu}
                    setSelectedCell={setSelectedCell}
                    rowIndex={'first'}
                    containerStyles={{ top: 0, bottom: 'px'}}
                  />
                </>
              )}
              {currentBlock && <GridRowOptions
                t={t}
                itemUUID={itemUUID}
                setSelectedRange={setSelectedRange}
                setContextMenu={setContextMenu}
                setSelectedCell={setSelectedCell}
                rowIndex={rowIndex}
              />}
            </tr>
          );
        })}
        {currentBlock && (
          <GridFormatting
            t={t}
            itemUUID={itemUUID}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
            setContextMenu={setContextMenu}
            startCell={startCell}
            setSelectedCell={setSelectedCell}
            tableRef={tableRef}
            scaling={scaling}
            parentUUID={parentUUID}
            copiedCellsRef={copiedCellsUUID}
            moveableRef={moveableRef}
          />
        )}
      </StaticTable>
      {currentBlock && (
        <ContextMenu
          documentId={`table-${itemUUID}`}
          scaling={1}
          swapElements={dummyCallback}
          handleModal={dummyCallback}
          multiSelectedElementIds={[]}
          showGenerateText={false}
          generateTextHandler={dummyCallback}
          assignVariableHandler={dummyCallback}
          staticTable={true}
          isStaticTableBlock={true}
        />
      )}
      {showContextMenu && (
        <GridContextMenu
          t={t}
          itemUUID={itemUUID}
          selectedRange={selectedRange}
          setSelectedRange={setSelectedRange}
          startCell={startCell}
          setSelectedCell={setSelectedCell}
          tableRef={tableRef}
          parentUUID={parentUUID}
          copiedCellsRef={copiedCellsUUID}
          setContextMenu={setContextMenu}
        />
      )}
    </PageElementsProvider>
    {(selectedElement?.contentType === FILLABLE_ELEMENT ||
      selectedElement?.contentType === ELEMENT_TYPES.CHART) &&
      ReactDOM.createPortal(
        <div
          className="layoutbtn-container"
          onClick={() => {
            updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
          }}
        >
          <Icon type={'close'} />
        </div>,
        document.getElementById('sidebar-absolute-close'),
      )}
    </ComplexEditingProvider>
    </CreativeBlockContainer>
  )
}

export default GridDesign;